import type { MarketplaceFulfillmentStatus } from "@nexus/contracts";
import {
  Button,
  Checkbox,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from "@nexus/ui";
import type { KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";

import { describeDeadline } from "../../lib/order-deadline";
import {
  fulfillmentOptionsFor,
  type OrdersDensity,
} from "../../lib/orders-list-model";
import { ORDERS_OVERDUE_ROW_CLASS } from "../../lib/orders-overdue-row";
import { canShowOrderLineReview } from "../../lib/order-line-hooks";
import { resolveOrderLineType } from "../../lib/order-line-kind";
import { describeOrderScript } from "../../lib/order-script-display";
import type {
  MarketplaceOrder,
  OrderScriptRow as OrderScriptRowData,
} from "../../types/marketplace.types";
import { DeadlineChip } from "./DeadlineChip";
import {
  ORDERS_CELL_SCRIPTS_CLASS,
  ORDERS_CELL_STATUS_CLASS,
  ORDERS_CELL_SENT_CLASS,
  ORDERS_CELL_DUE_CLASS,
  ORDERS_CELL_TYPE_CLASS,
  ORDERS_CHECKBOX_HIT_TARGET_CLASS,
  ORDERS_GRID_CLASS,
  ORDERS_ROW_PADDING_CLASS,
} from "./orders-list-grid";
import { OrderTypeBadge } from "./OrderTypeBadge";
import { ScriptActionButtons } from "./ScriptActionButtons";
import { StatusMenu } from "./StatusMenu";

interface OrderScriptRowProps {
  order: MarketplaceOrder;
  script: OrderScriptRowData;
  /** 1-based position — the only thing that separates identically titled scripts. */
  index: number;
  todayYmd: string;
  canEdit: boolean;
  selected: boolean;
  pending: boolean;
  uniformDeadline: boolean;
  density: OrdersDensity;
  chrome?: "marketplace" | "hub";
  tabIndex: 0 | -1;
  rowIndex: number;
  highlighted?: boolean;
  onToggleSelected: () => void;
  onFocusRow: () => void;
  onOpenDetails: () => void;
  onStatusChange: (next: MarketplaceFulfillmentStatus) => void;
  onDownloadAsset?: () => Promise<void>;
}

export function OrderScriptRow({
  order,
  script,
  index,
  todayYmd,
  canEdit,
  selected,
  pending,
  uniformDeadline,
  density,
  chrome = "marketplace",
  tabIndex,
  rowIndex,
  highlighted = false,
  onToggleSelected,
  onFocusRow,
  onOpenDetails,
  onStatusChange,
  onDownloadAsset,
}: OrderScriptRowProps) {
  const { t } = useTranslation();
  const display = describeOrderScript(
    script,
    t("marketplace.orders.detail.unnamedScript"),
  );
  const status = script.fulfillmentStatus;
  const canShowReview = canShowOrderLineReview(script);
  const showRevisionNote =
    status === "SENT_FOR_REVISION" && Boolean(script.revisionNote?.trim());
  const fullText = [display.title, display.subtitle]
    .filter(Boolean)
    .join("\n\n");
  const workingWeekdays = order.vendor.workingWeekdays;
  const deadline = describeDeadline(
    script.expectedDeliveryDate,
    todayYmd,
    workingWeekdays,
  );
  const isOverdue =
    script.fulfillmentStatus !== "CANCELLED" &&
    deadline?.tone === "overdue";

  const handleKeyDown = (event: KeyboardEvent<HTMLLIElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === " ") {
      event.preventDefault();
      if (canEdit) onToggleSelected();
    } else if (event.key === "Enter") {
      event.preventDefault();
      onOpenDetails();
    }
  };

  return (
    <li
      role="row"
      aria-level={2}
      aria-rowindex={rowIndex}
      data-script-row
      data-order-script-id={script.id}
      data-overdue={isOverdue ? "true" : undefined}
      data-tree-row
      data-tree-row-id={`script:${script.id}`}
      tabIndex={tabIndex}
      aria-selected={canEdit ? selected : undefined}
      aria-label={t("marketplace.orders.list.scriptRowLabel", {
        index,
        title: display.title,
      })}
      aria-current={highlighted ? "true" : undefined}
      onFocus={onFocusRow}
      onClick={onOpenDetails}
      onKeyDown={handleKeyDown}
      className={cn(
        ORDERS_GRID_CLASS,
        ORDERS_ROW_PADDING_CLASS,
        "group/script relative scroll-mt-20 cursor-pointer outline-none transition-colors",
        "focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/50",
        density === "compact" ? "min-h-12 py-1.5" : "min-h-16 py-2.5",
        chrome === "hub" && "px-4 py-3",
        highlighted
          ? "bg-primary-subtle"
          : selected
            ? "bg-primary-subtle/70"
            : "bg-muted/20 hover:bg-muted/45",
        !highlighted && isOverdue && ORDERS_OVERDUE_ROW_CLASS,
      )}
    >
      {chrome === "hub" ? null : (
        <>
          <span
            aria-hidden
            className="absolute bottom-0 left-[3.85rem] top-0 w-px bg-border"
          />
          <span
            aria-hidden
            className="absolute left-[3.85rem] top-1/2 h-px w-5 bg-border"
          />
        </>
      )}

      <span role="gridcell" className="z-10 flex items-center">
        {canEdit ? (
          <Checkbox
            checked={selected}
            aria-label={t("marketplace.orders.list.selectScript", { index })}
            onCheckedChange={onToggleSelected}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
            className={ORDERS_CHECKBOX_HIT_TARGET_CLASS}
          />
        ) : null}
      </span>

      <span role="gridcell" aria-hidden />

      <div role="gridcell" className={cn("min-w-0", chrome === "hub" ? "pl-0" : "pl-8")}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="min-w-0">
              <p
                className={cn(
                  "text-xs font-medium leading-4 text-foreground",
                  density === "compact" ? "line-clamp-1" : "line-clamp-2",
                )}
              >
                <span className="mr-1.5 tabular-nums text-muted-foreground/70">
                  {index}.
                </span>
                {display.title}
              </p>
              {display.subtitle ? (
                <div className="mt-0.5 flex min-w-0 items-baseline gap-1.5">
                  <span className="font-mono text-[10px] leading-4 tracking-label uppercase text-muted-foreground/70">
                    {t("marketplace.orders.list.drawer.hook")}
                  </span>
                  <p
                    className={cn(
                      "min-w-0 text-[11px] leading-4 text-muted-foreground",
                      density === "compact" ? "line-clamp-1" : "line-clamp-2",
                    )}
                  >
                    {display.subtitle}
                  </p>
                </div>
              ) : null}
            </div>
          </TooltipTrigger>
          <TooltipContent className="max-w-md whitespace-pre-line">
            {fullText}
          </TooltipContent>
        </Tooltip>
        {chrome === "hub" ? (
          <Button
            type="button"
            variant="secondary"
            size="xs"
            iconStart="eye"
            className="mt-3 bg-muted shadow-sm"
            aria-label={t("personaOpportunities.hub.scripts.viewAria", {
              title: display.title,
              defaultValue: `View ${display.title}`,
            })}
            onClick={(event) => {
              event.stopPropagation();
              onOpenDetails();
            }}
          >
            {t("personaOpportunities.hub.scripts.view", "View")}
          </Button>
        ) : null}
        {showRevisionNote ? (
          <p className="mt-1 flex items-start gap-1 text-[11px] text-warning-subtle-foreground">
            <Icon
              name="message-circle"
              size="xs"
              color="inherit"
              className="mt-0.5 shrink-0"
              aria-hidden
            />
            <span className="min-w-0 line-clamp-1">
              <span className="font-medium">
                {t("marketplace.orders.list.revisionNote")}:
              </span>{" "}
              {script.revisionNote}
            </span>
          </p>
        ) : null}
        <div className="mt-1 flex flex-wrap items-center gap-2 sm:hidden">
          <StatusMenu
            value={status}
            options={fulfillmentOptionsFor(order, status)}
            editable={canEdit}
            pending={pending}
            ariaLabel={t("marketplace.orders.list.statusMenuLabel", { index })}
            onChange={onStatusChange}
          />
          <DeadlineChip
            ymd={
              script.fulfillmentStatus === "CANCELLED"
                ? null
                : script.expectedDeliveryDate
            }
            todayYmd={todayYmd}
            workingWeekdays={workingWeekdays}
            muted
          />
        </div>
      </div>

      <span role="gridcell" className={ORDERS_CELL_TYPE_CLASS}>
        <OrderTypeBadge type={resolveOrderLineType(script)} />
      </span>

      <span
        role="gridcell"
        aria-hidden
        className={cn(
          ORDERS_CELL_SCRIPTS_CLASS,
          "text-xs text-muted-foreground/45",
        )}
      >
        —
      </span>

      <span role="gridcell" className={ORDERS_CELL_STATUS_CLASS}>
        <StatusMenu
          value={status}
          options={fulfillmentOptionsFor(order, status)}
          editable={canEdit}
          pending={pending}
          ariaLabel={t("marketplace.orders.list.statusMenuLabel", { index })}
          onChange={onStatusChange}
        />
      </span>

      <span
        role="gridcell"
        aria-hidden
        className={cn(
          ORDERS_CELL_SENT_CLASS,
          "text-xs text-muted-foreground/45",
        )}
      >
        —
      </span>

      <span role="gridcell" className={ORDERS_CELL_DUE_CLASS}>
        {uniformDeadline && deadline?.tone !== "overdue" ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-xs text-muted-foreground/45">—</span>
            </TooltipTrigger>
            <TooltipContent>
              {t("marketplace.orders.list.deadline.sameAsOrder")}
            </TooltipContent>
          </Tooltip>
        ) : (
          <DeadlineChip
            ymd={script.expectedDeliveryDate}
            todayYmd={todayYmd}
            workingWeekdays={workingWeekdays}
            muted
          />
        )}
      </span>

      <span role="gridcell">
        <ScriptActionButtons
          briefUrl={display.briefUrl}
          hasUpload={Boolean(display.uploadId)}
          scriptReviewId={canShowReview ? script.scriptReviewId : null}
          preflightUrl={canShowReview ? script.preflightUrl : null}
          sourceKind={script.customAssetKind === "BRIEF" ? "brief" : "script"}
          onDownloadAsset={onDownloadAsset}
          utilityClassName="opacity-0 transition-opacity group-hover/script:opacity-100 group-focus-within/script:opacity-100 [@media(hover:none)]:opacity-100"
          reviewAriaLabel={t("marketplace.orders.list.reviewVideoAria", {
            title: display.title,
            defaultValue: "Review video for {{title}} (opens in a new tab)",
          })}
        />
      </span>
    </li>
  );
}
